/* =====================================================================
   Alex & Lace — footer image band: arrival smoothing (ROADMAP §4).

   The footer band photo is below the fold (page bottom). Left to the browser's
   native lazy heuristic alone, on a real connection / with real high-res
   photography it can fetch + DECODE at the moment the band scrolls in — a
   main-thread decode stall (the "page pause") and a hard POP as the image
   appears. This module removes both WITHOUT baking the effect (the live plum
   duotone + halftone stay in CSS — swap-safe):

     · EARLY, CONTROLLED FETCH+DECODE — an IntersectionObserver with a GENEROUS
       rootMargin (2 viewports) fires well before the band enters; img.decode()
       forces the fetch (if the native lazy hasn't already) and the decode to
       happen THEN, off the entry frame — so the paint isn't a decode stall.
       (Native loading="lazy" + srcset stays on the <img> as the JS-off / no-IO
       fallback, so the image still loads without this script.)
     · FADE ARRIVAL — once decoded, the band fades the image from 0 → its rest
       opacity on the brand motion tokens, over the reserved band box (fixed
       height → zero CLS), replacing the pop. Reduced-motion → no fade, final
       state. Fail-open so the band is never left blank.

   Editor-swap-safe: image-agnostic (reads whatever the merchant sets); the live
   filter is untouched CSS.
   ===================================================================== */

(() => {
  const band = document.querySelector('[data-al-footer-band]');
  const img = band && band.querySelector('.al-footer__band-img');
  if (!band || !img) return; // no band image set → nothing to do (band shows the plum panel)

  // Arm the fade (JS-on only — JS-off never gets `--armed`, so CSS keeps the image visible).
  // Reduced-motion still arms + reveals; the fade transition itself is disabled in CSS (no motion),
  // so RM users get decode-ahead (no stall) and the image simply appears when ready.
  band.classList.add('al-footer__band--armed');

  let revealed = false;
  const reveal = () => {
    if (revealed) return;
    revealed = true;
    band.classList.add('al-footer__band--revealed');
    band.classList.remove('al-footer__band--armed'); // drop the will-change once the fade is set up
  };

  // Decode AHEAD of paint, then fade in. decode() also forces the fetch if the native lazy
  // hasn't started it yet — so both the download and the decode land off the entry frame.
  const decodeThenReveal = () => {
    if (img.decode) {
      img.decode().then(reveal).catch(reveal); // catch: broken image / abort → still reveal (fail-open)
    } else if (img.complete && img.naturalWidth > 0) {
      reveal();
    } else {
      img.addEventListener('load', reveal, { once: true });
      img.addEventListener('error', reveal, { once: true });
    }
  };

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, obs) => {
      if (entries.some((e) => e.isIntersecting)) { obs.disconnect(); decodeThenReveal(); }
    }, { root: null, rootMargin: '200% 0px 200% 0px' }); // ~2 viewports of runway → beats the scroll
    io.observe(band);
  } else {
    decodeThenReveal(); // no IO support → just decode-ahead + reveal
  }

  // Fail-open: never leave the band hidden if decode/IO hangs.
  setTimeout(reveal, 3000);
})();
